export type EpisodeTreeEpisode<S> = {
  id: number
  number: number
  title: string | null
  scenes: S[]
}

export type EpisodeTreeSeason<S> = {
  number: number
  episodes: EpisodeTreeEpisode<S>[]
}

export type EpisodeOption = {
  id: number
  label: string
  seasonNumber: number
  episodeNumber: number
}

/** episode_id из query. Без аргумента — window.location; иначе path/href от Inertia `page.url`. */
export function readEpisodeIdFromUrl(href?: string): number | null {
  let search: string | null = null
  if (href != null) {
    const q = href.indexOf("?")
    search = q >= 0 ? href.slice(q + 1) : ""
  } else if (typeof window !== "undefined") {
    search = window.location.search.replace(/^\?/, "")
  }
  if (search == null) return null
  const raw = new URLSearchParams(search).get("episode_id")
  if (!raw) return null
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : null
}

export function buildEpisodeOptions<S>(
  tree: EpisodeTreeSeason<S>[],
  formatLabel: (args: { season: number; episode: number; title: string }) => string,
): EpisodeOption[] {
  const opts: EpisodeOption[] = []
  for (const sn of tree) {
    for (const ep of sn.episodes) {
      opts.push({
        id: ep.id,
        seasonNumber: sn.number,
        episodeNumber: ep.number,
        label: formatLabel({
          season: sn.number,
          episode: ep.number,
          title: ep.title ?? "",
        }),
      })
    }
  }
  return opts
}

export function treeContainsEpisode<S>(tree: EpisodeTreeSeason<S>[], episodeId: number): boolean {
  return tree.some((season) => season.episodes.some((episode) => episode.id === episodeId))
}

export function findEpisodeScenes<S>(tree: EpisodeTreeSeason<S>[], episodeId: number): S[] {
  for (const sn of tree) {
    for (const ep of sn.episodes) {
      if (ep.id === episodeId) return ep.scenes
    }
  }
  return []
}

export function resolveEpisodeId(
  episodeOptions: EpisodeOption[],
  hints: { urlEpisodeId?: number | null; activeEpisodeId?: number | null },
): number {
  if (episodeOptions.length === 0) return 0

  const candidates = [hints.urlEpisodeId, hints.activeEpisodeId]
  for (const candidate of candidates) {
    if (candidate && episodeOptions.some((o) => o.id === candidate)) return candidate
  }

  return episodeOptions[0]?.id ?? 0
}

export function workspaceUrlWithEpisode(path: string, episodeId: number | null | undefined): string {
  if (!episodeId || episodeId <= 0) return path
  const url = new URL(path, typeof window !== "undefined" ? window.location.origin : "http://localhost")
  url.searchParams.set("episode_id", String(episodeId))
  return `${url.pathname}${url.search}`
}
